"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

export default function DocsBlocksListing() {
  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Listing
            </Text>
            <Badge variant="secondary">Block</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A flexible listing component for displaying collections of items.
            Supports various layouts, filtering, and pagination.
          </Text>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <SectionCard
          header={<CardTitle>Features</CardTitle>}
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">-</Badge>
              <Text as="p" className="text-sm">List/Grid layouts</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">-</Badge>
              <Text as="p" className="text-sm">Search filtering</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">-</Badge>
              <Text as="p" className="text-sm">Pagination</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">-</Badge>
              <Text as="p" className="text-sm">Sorting</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">-</Badge>
              <Text as="p" className="text-sm">Empty states</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">-</Badge>
              <Text as="p" className="text-sm">Loading states</Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Layout Options */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Layout Options</CardTitle>
              <CardDescription>Available display modes</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2 p-4 border rounded-lg">
              <Text as="p" className="font-medium">List View</Text>
              <Text as="p" className="text-sm text-muted-foreground">
                Vertical stacked items, ideal for detailed content and actions
              </Text>
              <div className="mt-2 space-y-2">
                <div className="h-8 bg-muted/50 rounded" />
                <div className="h-8 bg-muted/50 rounded" />
                <div className="h-8 bg-muted/50 rounded" />
              </div>
            </div>
            <div className="flex flex-col gap-2 p-4 border rounded-lg">
              <Text as="p" className="font-medium">Grid View</Text>
              <Text as="p" className="text-sm text-muted-foreground">
                Card-based grid, ideal for visual content and browsing
              </Text>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <div className="h-16 bg-muted/50 rounded" />
                <div className="h-16 bg-muted/50 rounded" />
                <div className="h-16 bg-muted/50 rounded" />
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Usage</CardTitle>
              <CardDescription>Basic listing implementation</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre>{`import { Listing } from "@/registry/new-york/blocks/listing/listing";

const items = [
  { id: 1, title: "Item 1", description: "Description" },
  { id: 2, title: "Item 2", description: "Description" },
];

<Listing
  items={items}
  layout="list"
  searchable
  renderItem={(item) => (
    <ListingItem
      title={item.title}
      description={item.description}
      actions={<Button>View</Button>}
    />
  )}
  emptyState={<EmptyState message="No items found" />}
/>`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={<CardTitle>Import</CardTitle>}
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`import { Listing } from "@/registry/new-york/blocks/listing/listing";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Default</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">items</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">T[]</td>
                <td className="py-2 font-mono text-xs">required</td>
                <td className="py-2 text-muted-foreground">Array of items to display</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">renderItem</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(item: T) =&gt; ReactNode</td>
                <td className="py-2 font-mono text-xs">required</td>
                <td className="py-2 text-muted-foreground">Render function for each item</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">layout</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">&quot;list&quot; | &quot;grid&quot;</td>
                <td className="py-2 font-mono text-xs">&quot;list&quot;</td>
                <td className="py-2 text-muted-foreground">Display layout</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">searchable</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Enable search</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">pagination</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Enable pagination</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">emptyState</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Content when no items</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">loading</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Show loading state</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
